/**
 * Top toolbar for the CreateLevels table with export and navigation controls.
 *
 * @module components/sim/createLevels/TopTableToolbar
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import FileSaver from 'file-saver';
import {
    Divider,
    Toolbar,
    Icon,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Link,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useToolbarStyles } from './CreateLevels';
import { downloadTemplate } from '../../../services/result.service';
import StyledLink from '../../common/StyledLink';
import Download from '../../../assets/images/icons/Download.png';
import Print from '../../../assets/images/icons/Print.png';
import FileCopyRoundedIcon from "@material-ui/icons/FileCopyRounded";

/**
 * TopTableToolbar is a React functional component that renders a toolbar to manage and interact with table-level operations.
 * It provides functionalities such as navigating through steps, uploading levels in an Excel file format, downloading an Excel template, and previewing and duplicating simulations.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.decreaseStep - Callback function to decrease the current step in the workflow.
 * @param {Object} props.data - The simulation data containing levels and associated properties.
 * @param {Function} props.increaseStep - Callback function to increase the current step in the workflow.
 * @param {Function} props.addLevels - Asynchronous handler to add new levels to the simulation data.
 * @param {Function} props.uploadLevels - Asynchronous handler to upload levels in bulk based on the selected Excel file.
 * @param {Object} props.newSim - Contains details about a new simulation being created.
 * @param {Function} props.setFlag - Updates a flag that indicates completion of the upload process.
 * @param {String} props.status - Current status code indicating validation result for Excel upload.
 * @param {Function} props.importLevel - Handler for importing levels from Excel files.
 * @param {Function} props.updateData - Function to update the data state after modifications.
 * @param {Function} props.handleDuplicateSim - Handler function to handle the duplication of an existing simulation.
 */
const TopTableToolbar = ({
    decreaseStep,
    data,
    increaseStep,
    addLevels,
    uploadLevels,
    newSim,
    setFlag,
    status,
    importLevel,
    handleDuplicateSim
}) => {
    const [open, setOpen] = React.useState(false);
    const [selectedFileName, setSelectedFileName] = React.useState('');
    const [selectedFile, setSelectedFile] = React.useState('');
    const classes = useToolbarStyles();
    const [excelStatus, setExcelStatus] = React.useState(status);
    const [previewDisabled, setPreviewDisabled] = React.useState(false);

    useEffect(() => {
        if (status === '400') {
            setExcelStatus(status);
        }
    }, [status]);

    useEffect(() => {
        if (excelStatus === '400' && selectedFileName !== '') {
            alert('More than 5 levels are not allowed for a sim.');
        }
    }, [selectedFileName, excelStatus]);

    useEffect( () => {
        let scripts = data?.levels?.filter(level => level.scripts?.length )?.length === 0 ?? true;
        setPreviewDisabled(scripts);
    }, [previewDisabled, setPreviewDisabled, data]);

    /**
     * An asynchronous function that processes the next step in a multi-step workflow.
     *
     * This function performs the following operations:
     * 1. Calls the `addLevels` function with the provided data.
     * 2. Increments the current step in the workflow using `increaseStep`.
     *
     * It is designed to handle workflow progression and ensure that required
     * operations are completed before moving to the next step.
     *
     * @function
     * @async
     * @returns {Promise<void>} Resolves once all operations are completed.
     */
    const handleNext = async () => {
        //console.log('Data = %o', data);

        await addLevels(data);
        increaseStep();
    };

    /**
     * Asynchronous function to handle the preview operation.
     *
     * This function performs the following actions:
     * 1. Calls the `addLevels` function with the provided data asynchronously.
     * 2. Stores the `sim_id` property from the data object into the local storage.
     * 3. Serializes the simulation data containing the `sim_id` into a JSON string
     *    and saves it in local storage under the key 'state'.
     *
     * @function
     * @async
     */
    const handlePreview = async () => {
        await addLevels(data);
        localStorage.setItem('sim_id', data.id);
        const simdata = JSON.stringify({ sim_id: data.id });
        localStorage.setItem('state', simdata);
    };

    /**
     * A function that handles the initialization process for uploading an Excel file.
     * It resets the current Excel status to an empty state and opens the upload dialog.
     */
    const HandleExcelUpload = () => {
        setExcelStatus('');
        setOpen(true);
    };

    /**
     * Handles the closing action for a given component or UI element.
     * Invokes logic to update the state, typically to close a modal, dropdown, or similar interface.
     * Sets the state to false to indicate the component is no longer open.
     */
    const handleClose = () => {
        setOpen(false);
    };

    /**
     * A callback function triggered when a file selection event occurs.
     * Updates the state with the name and object of the selected file.
     *
     * @param {Event} event - The event object containing details of the file selection.
     */
    const fileSelected = (event) => {
        setSelectedFileName(event.target.files[0].name);
        setSelectedFile(event.target.files[0]);
    };

    /**
     * Handles the process of uploading a file, updating the relevant data and UI states.
     *
     * This asynchronous function performs the following steps:
     * 1. Invokes the `importLevel` function to prepare for the upload process.
     * 2. Creates a `FormData` object and appends the selected file to it.
     * 3. Closes the current modal or dialog by updating the `open` state to false.
     * 4. Uploads the selected file and associated data to the server using the `uploadLevels` function.
     *    - The upload includes providing information such as simulation ID, number of levels,
     *      and the order in the last level's scripts (if any).
     * 5. Sets a flag to indicate the upload has been completed.
     * 6. Invokes the `importLevel` function again to refresh the imported level data.
     * 7. Closes any remaining open modal or dialog by calling the `handleClose` function.
     */
    const handleUpload = async () => {
        importLevel();
        const formData = new FormData();
        formData.append('file', selectedFile);
        setOpen(false);
        await uploadLevels(formData, newSim.id, data.levels.length,
            data.levels[data.levels.length - 1].scripts.length
                ? data.levels[data.levels.length - 1].order + 1 : 0);
        setFlag(true);
        importLevel();
        handleClose();
    };

    /**
     * Downloads an Excel template file from the server and prompts the user to save it locally.
     *
     * This function fetches the template file as a binary response, creates a Blob object
     * representing the Excel file, and saves it locally using the FileSaver library. The saved
     * file is named "Excel Template.xlsx".
     *
     * The function assumes that `downloadTemplate` is an asynchronous function that retrieves
     * the file data, and that the `FileSaver` library is available for handling file downloads
     * on the client side.
     */
    const templateDownload = () => {
        downloadTemplate().then((response) => {
            const fileName = 'Excel Template';
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
        });
    };

    return (
        <Toolbar className={classes.root}>
            <Typography
                className={classes.title}
                variant="h6"
                id="tableTitle"
                component="div"
            >
                Create Levels
            </Typography>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle className={classes.uploadDialog} id="form-dialog-title">
                    Upload Excel
                    <Link
                        variant="subtitle1"
                        underline="always"
                        className={classes.templateLink}
                        onClick={templateDownload}
                    >
                        Download Template
                    </Link>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please select the file with xlsx format only
                    </DialogContentText>
                    <Button color="primary" component="label">
                        Browse
                        {' '}
                        <input
                            hidden
                            type="file"
                            accept=".xlsx, .csv"
                            onChange={fileSelected}
                        />
                    </Button>
                    {selectedFileName || data.sheet_name}
                    <Divider variant="fullWidth" className={classes.excelDivider} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Button onClick={handleUpload} color="primary">
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
            <StyledLink
                to="/sims"
                target="_blank"
            >
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.previewBtn}
                    disableElevation
                    size="small"
                    onClick={handlePreview}
                    disabled={previewDisabled}
                >
                    PREVIEW
                </Button>
            </StyledLink>
            <Divider orientation="vertical" flexItem className={classes.divider} />
            <Tooltip title="Duplicate Sim">
                <IconButton className={classes.icon} onClick={handleDuplicateSim}>
                    <FileCopyRoundedIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title="Import">
                <IconButton className={classes.icon} onClick={HandleExcelUpload}>
                    <Icon>
                        <img src={Download} height={20} width={20} alt="download" />
                    </Icon>
                </IconButton>
            </Tooltip>
            <Tooltip title="Print">
                <IconButton className={classes.printIcon}>
                    <Icon>
                        <img src={Print} height={20} width={20} alt="print" />
                    </Icon>
                </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem className={classes.divider} />
            <Button
                variant="outlined"
                color="primary"
                className={classes.previousBtn}
                disableElevation
                onClick={() => decreaseStep()}
            >
                <ArrowBackIcon />
            </Button>
            <Button
                variant="contained"
                color="primary"
                className={classes.nextBtn}
                disableElevation
                size="small"
                onClick={handleNext}
            >
                NEXT
            </Button>
            <Divider />
        </Toolbar>
    );
};

TopTableToolbar.propTypes = {
    decreaseStep: PropTypes.func,
    data: PropTypes.object,
    increaseStep: PropTypes.func,
    addLevels: PropTypes.func,
    uploadLevels: PropTypes.func,
    newSim: PropTypes.object,
    setFlag: PropTypes.func,
    importLevel: PropTypes.func,
    status: PropTypes.any,
    handleDuplicateSim: PropTypes.func,
};

export default TopTableToolbar;
